
import React, { useState } from 'react';
import { X, Search, ChevronDown, ChevronUp } from 'lucide-react';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { useServerTags } from '@/hooks/useServerTags';
import { useLanguage } from '@/contexts/LanguageContext';

interface TagSelectorProps {
  selectedTags: string[];
  onTagsChange: (tags: string[]) => void;
}

export const TagSelector: React.FC<TagSelectorProps> = ({
  selectedTags,
  onTagsChange
}) => {
  const { t } = useLanguage();
  const { data: availableTags, isLoading } = useServerTags();
  const [searchTerm, setSearchTerm] = useState('');
  const [showAllTags, setShowAllTags] = useState(false);

  const term = searchTerm.toLowerCase();
  const filteredTags = availableTags?.filter(tag => 
    ((tag as any).searchTerms?.includes(term) || (tag.displayName || tag.name).toLowerCase().includes(term)) &&
    !selectedTags.includes(tag.name)
  ) || [];

  // Tags populaires à afficher en premier
  const popularTags = availableTags?.filter(tag => !selectedTags.includes(tag.name)).slice(0, 12) || [];

  const handleAddTag = (tagName: string) => {
    if (!selectedTags.includes(tagName)) {
      onTagsChange([...selectedTags, tagName]);
    }
    setSearchTerm('');
  };

  const handleRemoveTag = (tagName: string) => {
    onTagsChange(selectedTags.filter(tag => tag !== tagName));
  };

  if (isLoading) {
    return <div className="text-slate-400">{t('tags.loading')}</div>;
  }

  return (
    <div className="space-y-4">
      {/* Tags sélectionnés */}
      {selectedTags.length > 0 && (
        <div className="space-y-2">
          <h4 className="text-sm font-medium text-slate-300">{t('tags.selectedTags')}</h4>
          <div className="flex flex-wrap gap-2">
            {selectedTags.map((tag) => {
              const tagObj = availableTags?.find(t => t.name === tag);
              return (
                <Badge 
                  key={tag} 
                  variant="secondary"
                  className="bg-gaming-primary/20 border border-gaming-primary text-gaming-primary px-3 py-1 flex items-center gap-2 hover:bg-gaming-primary/30 transition-colors"
                >
                  {tagObj?.displayName || tag}
                  <Button
                    type="button"
                    variant="ghost"
                    size="sm"
                    className="h-auto p-0 hover:bg-transparent"
                    onClick={() => handleRemoveTag(tag)}
                  >
                    <X className="w-3 h-3" />
                  </Button>
                </Badge>
              );
            })}
          </div>
        </div>
      )}

      {/* Recherche */}
      <div className="space-y-3">
        <div className="relative">
          <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-slate-400" />
          <Input
            placeholder={t('tags.searchPlaceholder')}
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="bg-slate-800/50 border-slate-600 text-white pl-10 focus:border-gaming-primary focus:ring-gaming-primary/20"
          />
        </div>
        
        {/* Résultats de recherche */}
        {searchTerm && filteredTags.length > 0 && (
          <div className="bg-slate-800/80 backdrop-blur-sm border border-slate-600 rounded-lg shadow-xl z-50">
            <div className="p-2 border-b border-slate-600">
              <p className="text-xs text-slate-400">{t('tags.searchResults')} ({filteredTags.length})</p>
            </div>
            <div className="max-h-48 overflow-y-auto p-2 space-y-1">
              {filteredTags.slice(0, 20).map((tag) => (
                <Button
                  key={tag.id}
                  type="button"
                  variant="ghost"
                  size="sm"
                  className="w-full justify-start text-left hover:bg-gaming-primary/20 hover:text-gaming-primary text-white transition-colors"
                  onClick={() => handleAddTag(tag.name)}
                >
                  <span className="truncate">{tag.displayName || tag.name}</span>
                  {tag.description && (
                    <span className="ml-auto text-xs text-slate-400 hidden sm:block">
                      {tag.description.slice(0, 30)}...
                    </span>
                  )}
                </Button>
              ))}
            </div>
          </div>
        )}

        {/* Tags populaires/suggérés */}
        {!searchTerm && (
          <div className="space-y-3">
            <div className="flex items-center justify-between">
              <h4 className="text-sm font-medium text-slate-300">{t('tags.popular')}</h4>
              <Button
                type="button"
                variant="ghost"
                size="sm"
                onClick={() => setShowAllTags(!showAllTags)}
                className="text-gaming-accent hover:text-gaming-primary text-xs"
              >
                {showAllTags ? (
                  <>
                    {t('common.showLess')} <ChevronUp className="w-3 h-3 ml-1" />
                  </>
                ) : (
                  <>
                    {t('common.showMore')} <ChevronDown className="w-3 h-3 ml-1" />
                  </>
                )}
              </Button>
            </div>
            
            <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-2">
              {(showAllTags ? availableTags?.filter(tag => !selectedTags.includes(tag.name)) : popularTags)?.map((tag) => (
                <Button
                  key={tag.id}
                  type="button"
                  variant="outline"
                  size="sm"
                  className="h-auto py-2 px-3 border-slate-600 hover:border-gaming-primary hover:bg-gaming-primary/10 hover:text-gaming-primary text-slate-300 transition-all text-xs"
                  onClick={() => handleAddTag(tag.name)}
                >
                  <span className="mr-2" style={{ color: tag.color || '#64748b' }}>●</span>
                  <span className="truncate">{tag.displayName || tag.name}</span>
                </Button>
              ))}
            </div>
          </div>
        )}
      </div>

      <p className="text-xs text-slate-400">
        {t('tags.description')}
      </p>
    </div>
  );
};
